/**
 * The floor-plan symbols of doors, windows and openings, as the Figma board «Symbols of windows and
 * doors» draws them (file «Editor», node 7973:333486): a doorway is a light doorstep between two jamb
 * lines over the floors it joins, each half of the wall's thickness the floor of the room on its side
 * as the app's plan paints it (`OpeningFurnitureController` draggers), with the anchor line between
 * them; a door adds its leaves and their swings, a window is a white gap with frame posts, a glass
 * line and a mark between sashes, a sliding or folding door shows its panels in the doorway. Each is
 * drawn in the board's Default, Hover and Selected states; Hover-Selected differs only in the point
 * under the pointer.
 *
 * Which symbol an opening gets follows the catalog tags the app picks its plan sprite by
 * (`OpeningPlanTextures.TexturePath`, `FurnitureInfo.Is…Door`) and, for openings made in the editor,
 * their procedural style. The parts follow the rules Marina wrote beside the board («Правила условных
 * обозначений», node 7998:202253): what the 3D model says comes first (the leaves and sashes a
 * procedural style builds, the sash count a catalog window's tag names), and everything else is worked
 * out from the block's width W, so the symbol follows a resize live. The board's barn and pocket doors
 * have no tag or property to come from, so nothing selects them yet.
 *
 * Sizes are the board's, drawn 1 px = 1 cm, so they scale with the plan like the walls do; lines
 * stay one screen pixel.
 */
import type { Opening } from '../../core/hp/types';
import { openingKind } from '../../state/openingProperties';

export type OpeningSymbol =
  /** An arch, or anything that is neither door nor window: the doorstep alone. */
  | { family: 'doorway' }
  /** A catalog gap, not a way through: a plain white gap, as the app fills it like a window. */
  | { family: 'gap' }
  /** Swing leaves, their widths in metres from the hinge jamb: one, a main and a smaller one, or two equal. */
  | { family: 'door'; leaves: number[] }
  /** Equal sashes. */
  | { family: 'window'; sashes: number }
  /** A balcony block: a leaf `door` metres wide at the hinge end and a window of `sashes` beside it. */
  | { family: 'balcony'; door: number; sashes: number }
  /** A front door of `leaves` between side windows, or beside one, of `sashes` each. */
  | { family: 'entry'; leaves: number[]; windows: 1 | 2; sashes: number }
  /** Equal panels on staggered tracks inside the doorway; one panel across it is a garage door. */
  | { family: 'sliding'; leaves: number }
  /** An accordion door of `leaves` equal panels, folding against one jamb or, in two sets, against both. */
  | { family: 'folding'; sets: 1 | 2; leaves: number };

export type SymbolState = 'default' | 'hover' | 'selected';

/** The board's colours, by state; the names are its `wall/…` variables where it has one. */
export const SYMBOL_PALETTE: Record<SymbolState, {
  /** Jambs, window posts and mullions, leaf and panel outlines, the swing. */
  line: string;
  /** Leaves and panels (`wall/window-&-door---inactive`). */
  leaf: string;
  /** The doorstep over the ground. */
  step: string;
  /** The doorstep's anchor line. */
  axis: string;
  /** A window's gap (`wall/window-&-door---background`). */
  pane: string;
  glass: string;
}> = {
  default: { line: '#464955', leaf: '#cecfd6', step: 'rgba(255,255,255,0.5)', axis: 'rgba(255,255,255,0.4)', pane: '#ffffff', glass: '#d9d9d9' },
  hover: { line: '#2e3140', leaf: '#b9bbc7', step: 'rgba(255,255,255,0.4)', axis: 'rgba(255,255,255,0.4)', pane: '#e6e8ee', glass: '#c6c8d2' },
  selected: { line: '#3da2f7', leaf: 'rgba(123,194,253,0.8)', step: 'rgba(123,194,253,0.4)', axis: '#3da2f7', pane: '#ffffff', glass: 'rgba(123,194,253,0.8)' },
};

/** Marina's widths, metres: a sash of about 90 cm, an accordion panel of about 46, a side window of at least 30. */
const SASH = .9, PANEL = .46, SIDE_WINDOW = .3;
/**
 * A front or balcony door this wide or wider is taken for a block with windows beside it: the catalog
 * says which doors are front and balcony doors, not which of them have windows.
 */
const WITH_WINDOWS = 1.2;
/** Widths are compared with a tolerance, so a block set to exactly 100 or 140 cm is on the side the rules put it. */
const EPS = 1e-6;

/** How many parts of about `size` metres make `width`, never fewer than one. */
const parts = (width: number, size: number) => Math.max(1, Math.ceil(width / size - EPS));

/**
 * Marina's door (card 4): up to 100 cm one leaf; up to 140 a main leaf of min(90, ⅔W) and the rest;
 * from 140 two equal leaves. The main leaf is at the hinge jamb.
 */
export function doorLeaves(width: number): number[] {
  if (width <= 1 + EPS) return [width];
  if (width < 1.4 - EPS) { const main = Math.min(.9, width * 2 / 3); return [main, width - main]; }
  return [width / 2, width / 2];
}

/**
 * The symbol for an opening, from its catalog tags (none for a procedural opening, or while the
 * model's tags have not arrived) and what the editor knows of it.
 */
export function openingSymbol(opening: Opening, tags: readonly string[] = []): OpeningSymbol {
  const named = new Set(tags.map(tag => tag.toLowerCase()));
  const has = (tag: string) => named.has(tag.toLowerCase());
  const width = opening.width, style = opening.design?.style;
  // The app counts a wall-mounted, arched or garage door as a door whether or not it also says `Door`.
  const kind = has('Door') || has('WallDoor') || has('ArchDoor') || has('GarageDoor') ? 'door' : openingKind(opening, tags);
  // A style set in the editor builds the leaves and sashes 3D shows, whatever the catalog model was (`buildOpeningModel`).
  if (style && kind === 'window') return { family: 'window', sashes: style === 'triple' ? 3 : style === 'double' ? 2 : 1 };
  if (style && kind === 'door') return { family: 'door', leaves: style === 'double-door' ? [width / 2, width / 2] : [width] };
  if (has('GarageDoor')) return { family: 'sliding', leaves: 1 };
  if (has('Gap')) return { family: 'gap' };
  if (kind === 'window') {
    // A catalog window names its sashes; one tagged two ways names none.
    const counted = ([['SingleWindow', 1], ['DoubleWindow', 2], ['TripleWindow', 3]] as const).filter(([tag]) => has(tag));
    return { family: 'window', sashes: counted.length === 1 ? counted[0][1] : parts(width, SASH) };
  }
  if (kind !== 'door') return { family: 'doorway' };
  if (has('Folding')) {
    const leaves = parts(width, PANEL);
    return { family: 'folding', sets: width <= .92 + EPS ? 1 : 2, leaves: leaves + leaves % 2 };
  }
  if (has('Sliding') || has('Elevator')) return { family: 'sliding', leaves: Math.max(2, parts(width, SASH)) };
  if (has('BalconyDoor') && !has('Double') && width >= WITH_WINDOWS - EPS) return balconyBlock(width);
  if (has('Entry') && width >= (has('Double') ? 2 : WITH_WINDOWS) - EPS) return entryBlock(width, 2);
  return { family: 'door', leaves: doorLeaves(width) };
}

/**
 * Marina's balcony block (card 7): a leaf of 70 cm up to a 120 cm block and 80 beyond, the rest a
 * window of at least 30 cm in sashes of about 90; narrower than both, the parts share it in proportion.
 */
export function balconyBlock(width: number): OpeningSymbol {
  let door = width <= 1.2 + EPS ? .7 : .8, window = width - door;
  if (window < SIDE_WINDOW) { window = SIDE_WINDOW; door = width - window; }
  if (door < SIDE_WINDOW) door = window = width / 2;
  return { family: 'balcony', door, sashes: parts(window, SASH) };
}

/**
 * Marina's front door with windows (card 5): the door part is 0.6·W, between 90 and 180 cm, and leaves
 * each window at least 30 cm; its leaves follow the door rule, the windows share the rest.
 */
export function entryBlock(width: number, windows: 1 | 2): OpeningSymbol {
  let door = Math.min(Math.max(width * .6, .9), 1.8, width - SIDE_WINDOW * windows);
  if (door < SIDE_WINDOW) door = width / (windows + 1);
  return { family: 'entry', leaves: doorLeaves(door), windows, sashes: parts((width - door) / windows, SASH) };
}

/** Where a front door's parts lie along the opening: its door from `door[0]` to `door[1]`, the windows beside it. */
function entryLayout(symbol: Extract<OpeningSymbol, { family: 'entry' }>, width: number) {
  const door = symbol.leaves.reduce((sum, leaf) => sum + leaf, 0), side = symbol.windows === 2 ? (width - door) / 2 : 0;
  const windows: Array<[number, number]> = symbol.windows === 2 ? [[0, side], [side + door, width]] : [[door, width]];
  return { door: [side, side + door] as [number, number], windows };
}

/** A swing leaf: hinged at `hinge` along the opening, closing toward `toward`, `length` metres long. */
export interface SwingLeaf { hinge: number; toward: 1 | -1; length: number }

/** The leaves a symbol swings, for a `width` metres wide opening: the first at the hinge jamb, a second at the far one. */
export function swingLeaves(symbol: OpeningSymbol, width: number): SwingLeaf[] {
  const pair = (from: number, leaves: number[]): SwingLeaf[] => leaves.length === 1 ? [{ hinge: from, toward: 1, length: leaves[0] }]
    : [{ hinge: from, toward: 1, length: leaves[0] }, { hinge: from + leaves[0] + leaves[1], toward: -1, length: leaves[1] }];
  switch (symbol.family) {
    case 'door': return pair(0, symbol.leaves);
    case 'balcony': return [{ hinge: 0, toward: 1, length: symbol.door }];
    case 'entry': return pair(entryLayout(symbol, width).door[0], symbol.leaves);
    default: return [];
  }
}

/**
 * An accordion's zigzag, as the board draws it nearly shut: each panel its own width long, one set
 * reaching over 82 % of the doorway and each of two sets over 46 %, so how far the zigzag stands out
 * of the wall follows the panels, not the wall's thickness.
 */
function foldLayout(symbol: Extract<OpeningSymbol, { family: 'folding' }>, width: number) {
  const perSet = symbol.leaves / symbol.sets, panel = width / symbol.leaves;
  const reach = width * (symbol.sets === 1 ? .82 : .46), step = reach / perSet;
  return { perSet, reach, amplitude: Math.sqrt(Math.max(0, panel * panel - step * step)) };
}

/** The board's sliding panels as fractions of the width, [from, to, track], tracks counted from the side away from the swing. */
function slidingPanels(leaves: number): Array<[number, number, number]> {
  const board: Record<number, Array<[number, number, number]>> = {
    1: [[0, 1, 0]],
    2: [[0, .5566, .5], [.4444, 1, -.5]],
    3: [[0, .3542, 1], [.3333, .6875, 0], [.6458, 1, -1]],
    4: [[0, .2677, .5], [.2267, .4933, -.5], [.5067, .7733, -.5], [.7333, 1, .5]],
  };
  if (board[leaves]) return board[leaves];
  // More panels than the board draws: equal ones on two tracks, each overlapping its neighbours a little.
  const size = 1 / leaves, overlap = size * .08;
  return Array.from({ length: leaves }, (_, i): [number, number, number] => [Math.max(0, i * size - overlap), Math.min(1, (i + 1) * size + overlap), i % 2 ? -.5 : .5]);
}

/**
 * Where a symbol takes a tap, in its `u`/`v` metres (Marina's click zone, card 12): `'gap'` across the
 * wall with `margin` to spare off either face, and for an accordion over its folded panels; `'swing'`
 * in the rectangle the leaves swing through, as wide as the opening and as deep as its biggest leaf;
 * or nothing. `frame` and `angle` as for drawing.
 */
export function symbolHit(symbol: OpeningSymbol, size: { width: number; thickness: number; frame: number }, u: number, v: number, margin: number, angle = 90): 'gap' | 'swing' | null {
  // Along the wall only a little: the wall on either side keeps its own taps.
  if (u < -margin / 3 || u > size.width + margin / 3) return null;
  const folded = symbol.family === 'folding' ? foldLayout(symbol, size.width).amplitude / 2 + .02 : 0;
  if (Math.abs(v) <= Math.max(size.thickness / 2, folded) + margin) return 'gap';
  const leaves = swingLeaves(symbol, size.width);
  if (!leaves.length || v <= 0) return null;
  const open = angle * Math.PI / 180, depth = Math.max(...leaves.map(leaf => leaf.length)) * (open < Math.PI / 2 ? Math.sin(open) : 1);
  return v <= size.frame + depth + margin / 3 ? 'swing' : null;
}

/**
 * Where a symbol is drawn. `u` runs along the opening from its hinge jamb, `v` across the wall
 * from its axis toward the side the door swings to, both in metres.
 */
export interface SymbolFrame {
  at(u: number, v: number): { x: number; y: number };
  /** Screen pixels per metre. */
  px: number;
  /** Opening width and wall thickness, metres. */
  width: number;
  thickness: number;
  /** `v` of the line the leaves hinge on and the window frame sits on: the wall's axis unless recessed. */
  frame: number;
  /**
   * Fills the current path with what a doorway shows on one side of the wall's axis (`side` is the
   * sign of `v`): the floor of the room there, as the app's plan does.
   */
  ground(side: -1 | 1): void;
}

/**
 * Draws `symbol` in `frame`. `angle` is how far a leaf stands open, in degrees: the board's 90 unless
 * the door carries its own.
 */
export function drawOpeningSymbol(ctx: CanvasRenderingContext2D, frame: SymbolFrame, symbol: OpeningSymbol, state: SymbolState, angle = 90): void {
  const colors = SYMBOL_PALETTE[state], W = frame.width, t = frame.thickness, f = frame.frame;
  const pixel = 1 / frame.px, half = t / 2;
  // The gap covers the wall outline's outer half as well, so no face line runs across it.
  const pad = pixel / 2;
  const path = (points: Array<[number, number]>) => {
    ctx.beginPath();
    points.forEach(([u, v], i) => { const p = frame.at(u, v); if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y); });
    ctx.closePath();
  };
  const rect = (u0: number, v0: number, u1: number, v1: number) => path([[u0, v0], [u1, v0], [u1, v1], [u0, v1]]);
  const line = (u0: number, v0: number, u1: number, v1: number, color: string, width: number) => {
    const a = frame.at(u0, v0), b = frame.at(u1, v1);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.strokeStyle = color; ctx.lineWidth = width; ctx.stroke();
  };
  const panel = (points: Array<[number, number]>, stroke = 1) => {
    path(points); ctx.fillStyle = colors.leaf; ctx.fill();
    ctx.strokeStyle = colors.line; ctx.lineWidth = stroke; ctx.stroke();
  };

  /** The Doorstep: each half the floor on its side, under the state's veil, and the anchor line between them. */
  const step = (u0: number, u1: number) => {
    for (const side of [-1, 1] as const) { rect(u0, side < 0 ? -half - pad : 0, u1, side < 0 ? 0 : half + pad); frame.ground(side); }
    rect(u0, -half - pad, u1, half + pad); ctx.fillStyle = colors.step; ctx.fill();
    line(u0, 0, u1, 0, colors.axis, .5);
  };
  /** A window: the white gap, a post at each end, the glass between them and a mark between sashes. */
  const pane = (u0: number, u1: number, sashes: number) => {
    rect(u0, -half - pad, u1, half + pad); ctx.fillStyle = colors.pane; ctx.fill();
    const post = Math.min(Math.max(.06, 2 * pixel), (u1 - u0) / 4, t * .6);
    const glass = Math.min(Math.max(.02, pixel), post);
    rect(u0 + post, f - glass / 2, u1 - post, f + glass / 2); ctx.fillStyle = colors.glass; ctx.fill();
    ctx.fillStyle = colors.line;
    rect(u0, f - post / 2, u0 + post, f + post / 2); ctx.fill();
    rect(u1 - post, f - post / 2, u1, f + post / 2); ctx.fill();
    const along = Math.max(.04, 2 * pixel), across = Math.min(Math.max(.03, 1.5 * pixel), post);
    for (let k = 1; k < sashes; k++) {
      const c = u0 + post + (u1 - u0 - 2 * post) * k / sashes;
      rect(c - along / 2, f - across / 2, c + along / 2, f + across / 2); ctx.fill();
    }
  };
  /** Jamb lines across the wall; the unit's ends are drawn just inside the gap, whole. */
  const jambs = (at: number[]) => {
    for (const u of at) {
      const inset = u <= 0 ? pixel / 2 : u >= W ? -pixel / 2 : 0;
      line(u + inset, -half, u + inset, half, colors.line, 1);
    }
  };
  /** A swing leaf hinged at `hinge` on the frame line, closing toward `toward`: the swing, then the leaf over it. */
  const leaf = ({ hinge, toward, length }: SwingLeaf) => {
    const theta = angle * Math.PI / 180, cos = Math.cos(theta), sin = Math.sin(theta);
    const thickness = Math.min(Math.max(.06, 2 * pixel), length / 4);
    const tip: [number, number] = [hinge + toward * length * cos, f + length * sin];
    // Across the leaf, toward where it closes: inside the doorway when it stands at 90°.
    const across: [number, number] = [toward * sin * thickness, -cos * thickness];
    if (theta > 1e-3) {
      const centre = frame.at(hinge, f), closed = frame.at(hinge + toward * length, f), open = frame.at(tip[0], tip[1]);
      const from = Math.atan2(closed.y - centre.y, closed.x - centre.x), to = Math.atan2(open.y - centre.y, open.x - centre.x);
      let sweep = to - from;
      while (sweep > Math.PI) sweep -= 2 * Math.PI;
      while (sweep <= -Math.PI) sweep += 2 * Math.PI;
      ctx.beginPath(); ctx.arc(centre.x, centre.y, length * frame.px, from, from + sweep, sweep < 0);
      ctx.strokeStyle = colors.line; ctx.lineWidth = 1; ctx.stroke();
    }
    panel([[hinge, f], tip, [tip[0] + across[0], tip[1] + across[1]], [hinge + across[0], f + across[1]]]);
  };

  ctx.save();
  ctx.lineCap = 'butt'; ctx.lineJoin = 'miter';
  switch (symbol.family) {
    case 'doorway':
      step(0, W); jambs([0, W]);
      break;
    case 'gap':
      rect(0, -half - pad, W, half + pad); ctx.fillStyle = colors.pane; ctx.fill(); jambs([0, W]);
      break;
    case 'door':
      step(0, W); jambs([0, W]);
      swingLeaves(symbol, W).forEach(leaf);
      break;
    case 'window':
      pane(0, W, symbol.sashes); jambs([0, W]);
      break;
    case 'balcony':
      step(0, symbol.door); pane(symbol.door, W, symbol.sashes); jambs([0, symbol.door, W]);
      swingLeaves(symbol, W).forEach(leaf);
      break;
    case 'entry': {
      const layout = entryLayout(symbol, W);
      for (const [u0, u1] of layout.windows) pane(u0, u1, symbol.sashes);
      step(layout.door[0], layout.door[1]);
      jambs([...new Set([0, ...layout.door, W])]);
      swingLeaves(symbol, W).forEach(leaf);
      break;
    }
    case 'sliding': {
      step(0, W); jambs([0, W]);
      const thick = Math.min(Math.max(.06, 2 * pixel), t / 3), panels = slidingPanels(symbol.leaves);
      const tracks = Math.max(...panels.map(([, , track]) => track)) - Math.min(...panels.map(([, , track]) => track)) || 1;
      const spacing = Math.max(0, Math.min(.08, (t - thick) / tracks));
      for (const [a, b, track] of panels) {
        const v = -track * spacing;
        panel([[a * W, v - thick / 2], [b * W, v - thick / 2], [b * W, v + thick / 2], [a * W, v + thick / 2]]);
      }
      break;
    }
    case 'folding': {
      step(0, W); jambs([0, W]);
      const { perSet, reach, amplitude } = foldLayout(symbol, W), thick = Math.min(Math.max(.04, 1.5 * pixel), Math.max(amplitude, pixel) / 2);
      for (const [start, toward] of (symbol.sets === 1 ? [[0, 1]] : [[0, 1], [W, -1]]) as Array<[number, 1 | -1]>) {
        for (let i = 0; i < perSet; i++) {
          // A zigzag of one panel each, starting on the side away from the swing, as the board draws it.
          const a: [number, number] = [start + toward * reach * i / perSet, i % 2 ? amplitude / 2 : -amplitude / 2];
          const b: [number, number] = [start + toward * reach * (i + 1) / perSet, i % 2 ? -amplitude / 2 : amplitude / 2];
          const du = b[0] - a[0], dv = b[1] - a[1], span = Math.hypot(du, dv) || 1;
          const nu = -dv / span * thick / 2, nv = du / span * thick / 2;
          panel([[a[0] + nu, a[1] + nv], [b[0] + nu, b[1] + nv], [b[0] - nu, b[1] - nv], [a[0] - nu, a[1] - nv]], .5);
        }
      }
      break;
    }
  }
  ctx.restore();
}
